import { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export function InstallBanner() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSTip, setShowIOSTip] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) return;

    // Check if dismissed recently
    const lastDismissed = localStorage.getItem('pwa-banner-dismissed');
    if (lastDismissed) {
      const hours = (Date.now() - parseInt(lastDismissed)) / (1000 * 60 * 60);
      if (hours < 72) { setDismissed(true); return; }
    }

    // iOS detection
    const ua = navigator.userAgent;
    const iosDevice = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(iosDevice);

    const handler = (e: Event) => {
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      await deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') setDeferredPrompt(null);
    } else if (isIOS) {
      setShowIOSTip(true);
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    localStorage.setItem('pwa-banner-dismissed', Date.now().toString());
  };

  if (dismissed) return null;
  if (!deferredPrompt && !isIOS) return null;
  if (window.matchMedia('(display-mode: standalone)').matches) return null;

  return (
    <>
      <div className="fixed bottom-20 left-4 right-4 md:bottom-6 md:left-auto md:right-6 md:max-w-sm z-50 animate-in slide-in-from-bottom-4 duration-500">
        <div className="bg-primary text-primary-foreground rounded-2xl shadow-2xl p-4 flex items-center gap-3">
          <div className="bg-white/20 rounded-xl p-2.5 shrink-0">
            <Smartphone className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Instala UrgSalud</p>
            <p className="text-xs opacity-90 leading-tight">
              {isIOS ? 'Agrega la app a tu pantalla de inicio' : 'Acceso rápido como app nativa'}
            </p>
          </div>
          <Button
            size="sm"
            variant="secondary"
            className="shrink-0 rounded-xl font-semibold"
            onClick={handleInstall}
          >
            <Download className="h-4 w-4 mr-1" />
            Instalar
          </Button>
          <button onClick={handleDismiss} className="shrink-0 opacity-70 hover:opacity-100 transition-opacity">
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* iOS instruction modal */}
      {showIOSTip && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end md:items-center justify-center p-4" onClick={() => setShowIOSTip(false)}>
          <div className="bg-background rounded-2xl p-6 max-w-sm w-full space-y-4 animate-in slide-in-from-bottom-4" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-foreground">Instalar en iPhone/iPad</h3>
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="bg-primary/10 text-primary font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0">1</span>
                <span>Toca el botón <strong className="text-foreground">Compartir</strong> (ícono de cuadrado con flecha hacia arriba)</span>
              </li>
              <li className="flex gap-3">
                <span className="bg-primary/10 text-primary font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0">2</span>
                <span>Desliza y selecciona <strong className="text-foreground">"Agregar a pantalla de inicio"</strong></span>
              </li>
              <li className="flex gap-3">
                <span className="bg-primary/10 text-primary font-bold rounded-full w-6 h-6 flex items-center justify-center shrink-0">3</span>
                <span>Toca <strong className="text-foreground">"Agregar"</strong> para confirmar</span>
              </li>
            </ol>
            <Button className="w-full rounded-xl" onClick={() => setShowIOSTip(false)}>Entendido</Button>
          </div>
        </div>
      )}
    </>
  );
}
